import { A11yModule } from '@angular/cdk/a11y';
import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, TemplateRef, Type } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { createUniqueElementId } from '@kros-sk/shared/utils-fn/dom-utils';

import { KrosModalRef } from '../services/kros-modal-ref';

/**
 * The host of a modal opened by `KrosModalService.openCenteredShell()`. Unlike the legacy modal `KrosModalComponent`
 * it draws none of the `.kros-modal-*` markup: it gives the content the dialog role, the focus trap and Escape, and a
 * plain dialog surface — background, rounding and shadow — without dimensions of its own. The content sizes the dialog.
 *
 * The surface is what the user sees while the content has nothing to size the dialog with yet, such as a schema
 * still loading. Content that covers the whole viewport hides it: the shadow falls outside the viewport and the
 * content's own background covers the rounded corners.
 *
 * A modal opened as a sheet on a phone — the `sheetOnPhone` setting, which the schema modal host sets for an entry with
 * `phone: "drawer"` — makes the shell paint no surface and fill the viewport on a phone, so the content, a `modal-layout`
 * drawer, sits on the bottom edge with the backdrop showing above it. The shell is then what a tap beside the sheet
 * lands on, not the overlay's backdrop, so it
 * closes the modal the way the backdrop would (`closeOnBackdropClick`) — a tap that both starts and ends on the shell:
 * a text selection dragged out of the sheet ends there too, and its click goes to the shell as their common ancestor.
 * On a desktop the flag changes nothing.
 */
@Component({
  selector: 'kros-modal-shell',
  imports: [A11yModule, NgComponentOutlet, NgTemplateOutlet],
  templateUrl: './kros-modal-shell.component.html',
  styleUrl: './kros-modal-shell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[id]': 'id',
    role: 'dialog',
    tabindex: '-1',
    '(keydown.escape)': 'onEscapePress($event)',
    '(pointerdown)': 'onHostPointerDown($event)',
    '(click)': 'onHostClick($event)',
    '[class.kros-modal-shell--sheet-on-phone]': 'sheetOnPhone',
  },
})
export class KrosModalShellComponent {
  readonly #modalRef = inject(KrosModalRef);
  readonly #elementRef = inject(ElementRef<HTMLElement>);

  readonly id = createUniqueElementId();

  protected readonly sheetOnPhone = this.#modalRef.settings.sheetOnPhone ?? false;

  /** The press of the click under way started on the shell itself, not in the content. */
  #pressedOnHost = false;

  protected readonly allowFocusAutocapture = this.#modalRef.settings.allowFocusAutocapture;
  protected readonly template = this.#modalRef.content instanceof TemplateRef ? this.#modalRef.content : null;
  protected readonly component = this.#modalRef.content instanceof Type ? this.#modalRef.content : null;
  protected readonly contentInputs = this.#modalRef.settings.contentInputs;
  protected readonly templateContext = {
    data: this.#modalRef.data,
    cancel: this.#modalRef.cancel.bind(this.#modalRef),
    accept: this.#modalRef.submit.bind(this.#modalRef),
  };

  constructor() {
    this.#modalRef.overlay
      .keydownEvents()
      .pipe(takeUntilDestroyed())
      .subscribe((event) => this.#onOverlayKeydown(event));
  }

  onHostPointerDown(event: Event): void {
    this.#pressedOnHost = event.target === this.#elementRef.nativeElement;
  }

  /** A tap on the shell itself — beside a sheet on a phone — is a tap on the backdrop the shell covers there. */
  onHostClick(event: Event): void {
    const pressedOnHost = this.#pressedOnHost;
    this.#pressedOnHost = false;

    if (
      pressedOnHost &&
      this.sheetOnPhone &&
      this.#modalRef.settings.closeOnBackdropClick &&
      event.target === this.#elementRef.nativeElement
    ) {
      this.#modalRef.cancel('backdrop');
    }
  }

  // Escape: a copy of `KrosModalComponent.onEscapePress()`, `#onOverlayKeydown()` and `#isEscapeAddressedToModal()`.
  // Keep the two in step until they are shared.

  /**
   * A layer inside the modal that already acted on Escape - a DevExtreme dropdown closing its list -
   * only marks the event as handled without stopping it, so the modal has to leave such events alone.
   */
  onEscapePress(event: Event): void {
    const isAutoRepeat = event instanceof KeyboardEvent && event.repeat;

    if (isAutoRepeat || event.defaultPrevented || !this.#modalRef.settings.closeOnEscClick || !this.#isEscapeAddressedToModal(event)) {
      return;
    }

    this.#modalRef.cancel();
    event.stopPropagation();
  }

  #onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.onEscapePress(event);
    }
  }

  /**
   * The overlay keyboard dispatcher delivers Escape regardless of where focus is, so the modal has
   * to decide what belongs to it: its own content and the page behind it, where modals opened
   * without focus autocapture leave focus. A layer above the modal - a DevExtreme popup or another
   * overlay - keeps handling Escape itself, and focus lost to the body belongs to nobody.
   */
  #isEscapeAddressedToModal(event: Event): boolean {
    const target = event.target;

    if (!(target instanceof Element)) {
      return false;
    }

    if (this.#elementRef.nativeElement.contains(target)) {
      return true;
    }

    if (target === document.body || target === document.documentElement) {
      return false;
    }

    return !target.closest('.cdk-overlay-container, .dx-overlay-wrapper');
  }
}
